/**
 * PL ①シート版(社内ドラフト。2026-09-10 指示 → 2026-09-10 夜に「1日シートと全く同じUIで 日/週/月、横軸は会社別」で再作成)。
 * 現行「PL日報」の「1日」シートに寄せた表: 左に 大分類／中分類／項目 の3列、右に 会社列＋合計。
 * 行の並び・名前はシートのとおり(構成比 → アポ数 → 売上 → 売上原価 → 粗利 → 経費 → 営業利益 → 営業外 → 経常利益)。
 * 期間は画面上部の 日次／週次／月次(その期間の合計を1枚のシートとして出す)。見出しは固定、行クリックでアポインター別の内訳。
 */
import HelpTip, { type Help } from '@core/components/HelpTip';
import { IconChevronRight } from '@core/components/icons';
import type { Company } from '@core/lib/types';
import { HELP, costRateClass, costRateVerdict } from './colors';
import { fmtCellUnit, TONE_LABELS, type MoneyUnit, type PlRow } from './plRow';

/** 区分の見出し行(「売上原価 ／ アポ報酬」)を 大分類・中分類 に分ける */
function splitSection(label: string): { cat1: string; cat2: string } {
  const parts = label.split('／').map((s) => s.trim());
  return { cat1: parts[0] ?? '', cat2: parts.slice(1).join(' / ') };
}

function helpOf(r: PlRow): Help | null {
  if (!r.hint) return null;
  return { title: r.label, what: r.hint, better: r.better ?? '—' };
}

export default function PlSheetView({
  rows,
  cols,
  open,
  toggle,
  costTarget,
  unit,
}: {
  rows: PlRow[];
  cols: Company[];
  open: Set<string>;
  toggle: (k: string) => void;
  costTarget: number;
  /** 金額の単位(円／千円／万円。画面上部の切替) */
  unit: MoneyUnit;
}) {
  /** 直前の見出し行から引き継ぐ 大分類・中分類 */
  let cat1 = '';
  let cat2 = '';

  const valueCells = (r: PlRow) => {
    const cell = (v: number | null, isTotal = false, key?: string) => {
      const verdict = r.colorRate ? costRateVerdict(v, costTarget) : null;
      const text = fmtCellUnit(v, r.fmt, unit);
      return (
        <td key={key} className={`num ${isTotal ? 'pl-total ' : ''}${r.colorRate ? costRateClass(v) : ''}`}>
          {verdict ? (
            <HelpTip help={HELP.costRate} verdict={verdict} value={text}>
              {text}
            </HelpTip>
          ) : (
            text
          )}
        </td>
      );
    };
    return (
      <>
        {cols.map((c) => cell(r.values[c.id] ?? null, false, c.id))}
        {cell(r.total, true)}
      </>
    );
  };

  // 1) まず行を並べる(見出し行は 大分類・中分類 に読み替えて消す。開いている行の下に内訳を入れる)
  //    大分類は科目のまとまり(tone)から決める。シートと同じく 粗利・営業利益・経常利益 もそれぞれ独立した帯になる
  type Line = { row: PlRow; cat1: string; cat2: string; tone: string; child: boolean; summary: boolean };
  const lines: Line[] = [];
  for (const r of rows) {
    if (r.section) {
      const s = splitSection(r.label);
      cat1 = s.cat1;
      cat2 = s.cat2;
      continue;
    }
    const tone = r.tone ?? '';
    const big = TONE_LABELS[tone] ?? cat1;
    // 小計・合計・率の行は中分類を空にして、行まるごと大分類の色にする(2026-09-11 の見本)
    const mid = r.summary ? '' : big === cat1 ? cat2 : '';
    lines.push({ row: r, cat1: big, cat2: mid, tone, child: false, summary: !!r.summary });
    if ((r.children?.length ?? 0) > 0 && open.has(r.key)) {
      for (const c of r.children!) lines.push({ row: c, cat1: big, cat2: mid, tone, child: true, summary: false });
    }
  }
  // 2) 同じ 大分類・中分類 が続く分はセルを縦につなぐ(シートの結合セルと同じ。0=前の行につながる)
  const span1 = new Array(lines.length).fill(0) as number[];
  const span2 = new Array(lines.length).fill(0) as number[];
  for (let i = 0; i < lines.length; ) {
    let j = i;
    while (j < lines.length && lines[j]!.cat1 === lines[i]!.cat1) j++;
    span1[i] = j - i;
    i = j;
  }
  for (let i = 0; i < lines.length; ) {
    let j = i;
    while (j < lines.length && lines[j]!.cat1 === lines[i]!.cat1 && lines[j]!.cat2 === lines[i]!.cat2 && lines[j]!.summary === lines[i]!.summary) j++;
    span2[i] = j - i;
    i = j;
  }

  const body: React.ReactNode[] = lines.map((ln, i) => {
    const r = ln.row;
    const has = !ln.child && (r.children?.length ?? 0) > 0;
    const isOpen = open.has(r.key);
    const help = ln.child ? null : helpOf(r);
    return (
      <tr
        key={r.key}
        className={`${has ? 'clickable' : ''}${span1[i]! > 0 ? ' grp-start' : ''}${r.bold ? ' pl-bold' : ''}${r.summary ? ' pl-summary' : ''}${ln.child ? ' pl-child' : ''}${ln.tone ? ` pl-t-${ln.tone}` : ''}`}
        onClick={has ? () => toggle(r.key) : undefined}
        aria-expanded={has ? isOpen : undefined}
      >
        {span1[i]! > 0 && (
          <td className="cat cat1" rowSpan={span1[i]}>
            {ln.cat1}
          </td>
        )}
        {span2[i]! > 0 && (
          <td className="cat cat2" rowSpan={span2[i]}>
            {ln.cat2}
          </td>
        )}
        <td className="wrap" style={ln.child ? { paddingLeft: 26 } : undefined}>
          {ln.child ? (
            <span className="hint" style={{ display: 'inline-block', width: 14 }}>
              └
            </span>
          ) : has ? (
            <span className={`chev${isOpen ? ' open' : ''}`}>
              <IconChevronRight size={12} />
            </span>
          ) : (
            <span style={{ display: 'inline-block', width: 14 }} />
          )}
          {help ? <HelpTip help={help}>{r.label}</HelpTip> : r.label}
        </td>
        {valueCells(r)}
      </tr>
    );
  });

  // 列幅は固定(会社の列はすべて同じ幅。2026-09-11 指示)。大分類・中分類は縦書きで細く
  const W = { cat1: 26, cat2: 26, item: 210, company: 92, total: 100 };
  const width = W.cat1 + W.cat2 + W.item + cols.length * W.company + W.total;

  return (
    <div className="table-scroll">
      {/* 幅は CSS 変数で渡す(スマホの縦積みでは base.css 側の幅指定が勝つようにするため) */}
      <table className="fit-table pl-table pl-sheet" style={{ '--pl-w': `${width}px` } as React.CSSProperties}>
        <colgroup>
          <col style={{ width: W.cat1 }} />
          <col style={{ width: W.cat2 }} />
          <col style={{ width: W.item }} />
          {cols.map((c) => (
            <col key={c.id} style={{ width: W.company }} />
          ))}
          <col style={{ width: W.total }} />
        </colgroup>
        <thead>
          <tr>
            {/* 大分類・中分類の見出しは出さない(現行シートも空欄。2026-09-11 指示) */}
            <th className="cat" aria-label="大分類" />
            <th className="cat" aria-label="中分類" />
            <th>項目</th>
            {cols.map((c) => (
              <th key={c.id} className="num">
                {c.name}
              </th>
            ))}
            <th className="num pl-total">合計</th>
          </tr>
        </thead>
        <tbody>{body}</tbody>
      </table>
    </div>
  );
}
